import { useLocalization } from '@magicdoorinc/ui';
import { createMemo } from 'solid-js';
import { filterNavigationItems, NAVIGATION_CONFIG } from '~/config/navigation';
import { useCurrentCompany } from '~/contexts/global';
import type { NavigationItem } from '~/config/navigation';

export enum ACTIONS {
  READ = 'read',
  WRITE = 'write',
  DELETE = 'delete',
}

export type Resource = {
  name: string;
  permissionKey?: string;
  displayName: string;
  routePath?: string;
  children?: Resource[];
};

function navigationItemToResource(item: NavigationItem, t: (key: string) => string): Resource {
  return {
    name: item.resourceName,
    permissionKey: item.permissionKey,
    displayName: t(item.id),
    routePath: item.path,
    children: item.children?.map((child) => navigationItemToResource(child, t)),
  };
}

function buildResources(t: (key: string) => string, checkFeature: (feature: any) => boolean): Resource[] {
  const filteredNavItems = filterNavigationItems(NAVIGATION_CONFIG, checkFeature);
  return filteredNavItems.map((item) => navigationItemToResource(item, t));
}

export type FlattenedResource = Omit<Resource, 'children'> & {
  fullName: string;
  parentName?: string;
};

export const useResources = () => {
  const { t } = useLocalization();
  const { checkFeature } = useCurrentCompany();

  const resources = createMemo(() => buildResources(t, checkFeature));
  const flattenedResources = createMemo(() => toFlattenedResources(resources()));

  return {
    resources,
    flattenedResources,
  };
};

export const createPermission = (resource: string, action: ACTIONS | '*'): string => {
  return `${resource}:${action}`;
};

export const getResourcePermissionKey = (resource: Resource, parentName?: string): string => {
  return resource.permissionKey ?? (parentName ? `${parentName}_${resource.name}` : resource.name);
};

function toFlattenedResources(resourceList: Resource[], parentName?: string): FlattenedResource[] {
  const result: FlattenedResource[] = [];

  for (const resource of resourceList) {
    const fullName = resource.permissionKey ?? (parentName ? `${parentName}_${resource.name}` : resource.name);

    result.push({
      name: resource.name,
      displayName: resource.displayName,
      routePath: resource.routePath,
      fullName,
      parentName,
    });

    if (resource.children) {
      result.push(...toFlattenedResources(resource.children, fullName));
    }
  }

  return result;
}
